import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideIcons } from '@ng-icons/core';
import { NG_ICONS } from '@shared/design/atoms/icons/icon-source.config';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  NavigationStart,
  provideRouter,
  Router,
  withPreloading,
  PreloadAllModules,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { infrastructureProviders } from '@infrastructure/infrastructure';
import { routes } from '@main/app.routes';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import Aura from '@primeng/themes/aura';
import { definePreset } from '@primeng/themes';
import { errorInterceptor } from '@core/interceptors/error.interceptor';
import { refreshInterceptor } from '@core/interceptors/refresh.interceptor';
import { ThemeService } from '@core/services/presenters/theme.service';
import {
  PRIMENG_DARK_COLOR_SCHEME,
  THEME_CLASS,
} from '@shared/utils/constants/theme.constant';

// Aura aplica por defecto una sombra sutil a todos los form fields
// (`0 1px 2px 0 rgba(18, 18, 23, 0.05)`). En el diseño de Kipey los inputs son
// planos: el borde ya delimita el control, así que se anula en el token para
// que quede consistente en inputs, selects, textareas y datepickers.
// Aura declara el shadow tanto en `semantic.formField` como dentro de cada
// colorScheme, y este último se emite después, así que hay que anular ambos.
// El scale de `surface` y los tokens `text`/`primary` de `colorScheme.dark`
// deben espejar la paleta night/primary de tailwind.config.js (dark mode):
// heredan a formField/content/overlay a través de las referencias internas
// de Aura (p.ej. `formField.background: "{surface.950}"`).
const KipeyAura = definePreset(Aura, {
  semantic: {
    formField: {
      shadow: 'none',
    },
    colorScheme: {
      light: {
        formField: {
          shadow: 'none',
        },
      },
      dark: PRIMENG_DARK_COLOR_SCHEME,
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideIcons(NG_ICONS),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: KipeyAura,
        options: {
          darkModeSelector: `.${THEME_CLASS}`,
          cssLayer: false,
        },
      },
      translation: {
        dayNames: [
          'Domingo',
          'Lunes',
          'Martes',
          'Miércoles',
          'Jueves',
          'Viernes',
          'Sábado',
        ],
        dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
        dayNamesMin: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
        monthNames: [
          'Enero',
          'Febrero',
          'Marzo',
          'Abril',
          'Mayo',
          'Junio',
          'Julio',
          'Agosto',
          'Septiembre',
          'Octubre',
          'Noviembre',
          'Diciembre',
        ],
        monthNamesShort: [
          'Ene',
          'Feb',
          'Mar',
          'Abr',
          'May',
          'Jun',
          'Jul',
          'Ago',
          'Sep',
          'Oct',
          'Nov',
          'Dic',
        ],
        today: 'Hoy',
        clear: 'Limpiar',
        dateFormat: 'dd/mm/yy',
        weekHeader: 'Sem',
        choose: 'Elegir',
        upload: 'Subir',
        cancel: 'Cancelar',
        pending: 'Pendiente',
        fileSizeTypes: ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
        chooseYear: 'Elegir año',
        chooseMonth: 'Elegir mes',
        chooseDate: 'Elegir fecha',
        prevDecade: 'Década anterior',
        nextDecade: 'Próxima década',
        prevYear: 'Año anterior',
        nextYear: 'Próximo año',
        prevMonth: 'Mes anterior',
        nextMonth: 'Próximo mes',
        prevHour: 'Hora anterior',
        nextHour: 'Próxima hora',
        prevMinute: 'Minuto anterior',
        nextMinute: 'Próximo minuto',
        prevSecond: 'Segundo anterior',
        nextSecond: 'Próximo segundo',
        am: 'AM',
        pm: 'PM',
        emptyMessage: 'Sin resultados',
        emptyFilterMessage: 'Sin resultados',
        emptySearchMessage: 'Sin resultados',
        emptySelectionMessage: 'Sin selección',
      },
    }),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            staleTime: 1000 * 5,
            retry: 1,
          },
        },
      }),
    ),
    provideAppInitializer(() => {
      const router = inject(Router);
      const queryClient = inject(QueryClient);
      router.events
        .pipe(filter((e) => e instanceof NavigationStart))
        .subscribe(() => {
          queryClient
            .getQueryCache()
            .getAll()
            .forEach((query) => {
              [...query.observers].forEach((observer) => observer.destroy());
            });
        });
    }),
    provideAppInitializer(() => {
      inject(ThemeService).bootstrap();
    }),
    ...infrastructureProviders,
    provideHttpClient(
      withInterceptors([AuthInterceptor, refreshInterceptor, errorInterceptor]),
    ),
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'en-US' },
  ],
};
